import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, BookOpen, Highlighter, StickyNote, PenLine, Clock,
  ChevronLeft, ChevronRight, Sparkles, Brain, ArrowLeft
} from "lucide-react";
import { motion } from "framer-motion";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function StatBlock({ icon: Icon, label, value, color }) {
  return (
    <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-4">
      <div
        className={`w-10 h-10 rounded-lg border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-2`}
        style={{ backgroundColor: color }}
      >
        <Icon className="w-5 h-5 text-neo-text" />
      </div>
      <p className="font-heading font-extrabold text-2xl dark:text-white" data-testid={`summary-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">{label}</p>
    </div>
  );
}

export default function MonthlySummary({ api }) {
  const navigate = useNavigate();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSummary = async (m, y) => {
    setLoading(true);
    try {
      const res = await axios.get(`${api}/monthly-summary?month=${m}&year=${y}`, { withCredentials: true });
      setData(res.data);
    } catch {
      toast.error("Failed to load monthly summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary(month, year);
  }, [api, month, year]);

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div className="space-y-6" data-testid="monthly-summary-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white" data-testid="summary-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl dark:text-white">Monthly Summary</h1>
            <p className="text-sm text-neo-text-secondary dark:text-gray-400">AI-powered reading insights</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white p-2" data-testid="summary-prev-month">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="font-heading font-bold text-lg dark:text-white min-w-[160px] text-center" data-testid="summary-month-label">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <button
            onClick={goNext}
            disabled={isCurrentMonth}
            className={`neo-btn p-2 ${isCurrentMonth ? "bg-gray-200 text-gray-400 border-gray-300 shadow-none cursor-not-allowed" : "bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white"}`}
            data-testid="summary-next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
            <div className="w-10 h-10 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="font-heading font-bold dark:text-white">Generating your summary...</p>
          </div>
        </div>
      ) : data ? (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatBlock icon={BookOpen} label="Papers Started" value={data.papers_started} color="#7DD3FC" />
            <StatBlock icon={BookOpen} label="Completed" value={data.papers_completed} color="#86EFAC" />
            <StatBlock icon={Brain} label="Pages Read" value={data.total_pages} color="#C4B5FD" />
            <StatBlock icon={Highlighter} label="Highlights" value={data.total_highlights} color="#FDE047" />
            <StatBlock icon={StickyNote} label="Notes" value={data.total_notes} color="#FFD836" />
            <StatBlock icon={Clock} label="Read Time" value={formatTime(data.total_reading_seconds || 0)} color="#FF6B6B" />
          </div>

          {/* AI Insight */}
          {data.ai_insight && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] border-neo-purple border-[3px]"
            >
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-neo-purple" />
                <h3 className="font-heading font-bold dark:text-white">AI Reading Coach</h3>
              </div>
              <p className="text-sm dark:text-gray-200 leading-relaxed" data-testid="ai-insight">{data.ai_insight}</p>
            </motion.div>
          )}

          {/* Papers List */}
          {data.paper_titles?.length > 0 && (
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
              <h3 className="font-heading font-bold dark:text-white mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-neo-blue" />
                Papers This Month
              </h3>
              <div className="space-y-2">
                {data.paper_titles.map((title, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm dark:text-gray-300">
                    <BookOpen className="w-3 h-3 text-neo-text-secondary dark:text-gray-400 flex-shrink-0" />
                    {title}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reflections count */}
          {data.total_reflections > 0 && (
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-neo-purple border-2 border-neo-border dark:border-[#444] flex items-center justify-center flex-shrink-0">
                <PenLine className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-heading font-bold dark:text-white">{data.total_reflections} Reflections Written</p>
                <p className="text-xs text-neo-text-secondary dark:text-gray-400">
                  Earned {data.total_reflections * 25} XP from reflecting on your reading
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {data.papers_started === 0 && (
            <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center py-8">
              <Calendar className="w-12 h-12 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
              <p className="font-heading font-bold dark:text-white mb-1">No reading activity this month</p>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400">
                Upload a paper and start reading to see your monthly summary!
              </p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
